import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo } from "react";

import { buildImpedimentoAlertMsgParams } from "@utils/authErrors";
import { BettoAlertModal } from "./BettoAlertModal.jsx";
import { resolveBettoAlert } from "./resolveBettoAlert";

const formatAlertCopy = (text) => {
	if (!text || typeof text !== "string" || text.includes("<")) {
		return text;
	}

	if (text === text.toUpperCase() && /[A-Z]/.test(text)) {
		return text.charAt(0) + text.slice(1).toLowerCase();
	}

	return text;
};

const formatAlertTitle = (text) => {
	if (!text || typeof text !== "string") {
		return text;
	}

	if (text === text.toUpperCase()) {
		return text.charAt(0) + text.slice(1).toLowerCase();
	}

	return text;
};

const GENERIC_TITLES = new Set([
	"ERROR",
	"SUCCESS",
	"SUCCESSFULL_TITLE",
	"SECURITY",
	"LUCKY_BOX_MOBILE",
	"PROFILE_UPDATE_REQUIRED_TITLE",
]);

const getAlertTitle = (alertData, resolvedAlert, translate) => {
	if (alertData.noTranslate && alertData.title && !GENERIC_TITLES.has(alertData.title)) {
		return formatAlertTitle(alertData.title);
	}

	if (alertData.title && !GENERIC_TITLES.has(alertData.title)) {
		const translatedTitle = translate(alertData.title);
		if (translatedTitle && translatedTitle !== alertData.title) {
			return formatAlertTitle(translatedTitle);
		}
	}

	return formatAlertTitle(translate(resolvedAlert.titleKey));
};

const getAlertMessage = (alertData, resolvedAlert, translate) => {
	if (alertData.noTranslate) {
		return formatAlertCopy(alertData.msg);
	}

	const shouldUseVariantMessage =
		resolvedAlert.fallbackMsgKeys?.includes(alertData.msg) ||
		(resolvedAlert.fallbackMsgKey && alertData.msg === resolvedAlert.fallbackMsgKey);

	if (shouldUseVariantMessage) {
		if (alertData.msgParams) {
			return formatAlertCopy(
				translate(
					resolvedAlert.messageKey,
					buildImpedimentoAlertMsgParams(alertData.msgParams, translate) || {},
					{ renderInnerHtml: true }
				)
			);
		}

		return formatAlertCopy(translate(resolvedAlert.messageKey));
	}

	if (alertData.msgParams) {
		return formatAlertCopy(
			translate(
				alertData.msg,
				buildImpedimentoAlertMsgParams(alertData.msgParams, translate) || {},
				{ renderInnerHtml: true }
			)
		);
	}

	if (alertData.msg) {
		const translatedMessage = translate(alertData.msg);
		if (translatedMessage && translatedMessage !== alertData.msg) {
			return formatAlertCopy(translatedMessage);
		}
	}

	return formatAlertCopy(translate(resolvedAlert.messageKey));
};

export const BettoAlertItem = memo(({ alertData, index, translate, onClose, onButtonAction }) => {
	const resolvedAlert = useMemo(() => resolveBettoAlert(alertData), [alertData]);

	const buttons = useMemo(() => {
		if (!resolvedAlert) {
			return [];
		}

		return resolvedAlert.buttons.map((button) => ({
			...button,
			label: translate(button.labelKey),
		}));
	}, [resolvedAlert, translate]);

	const handleClose = useCallback(() => {
		onClose(index);
	}, [index, onClose]);

	const handleButtonClick = useCallback(
		(button) => {
			onButtonAction(index, alertData, button);
		},
		[alertData, index, onButtonAction]
	);

	if (!resolvedAlert) {
		return null;
	}

	return (
		<BettoAlertModal
			title={getAlertTitle(alertData, resolvedAlert, translate)}
			message={getAlertMessage(alertData, resolvedAlert, translate)}
			illustrationKey={resolvedAlert.illustrationKey}
			buttons={buttons}
			showClose={!alertData.persistent}
			onClose={handleClose}
			onButtonClick={handleButtonClick}
		/>
	);
});

BettoAlertItem.displayName = "BettoAlertItem";

BettoAlertItem.propTypes = {
	alertData: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	translate: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	onButtonAction: PropTypes.func.isRequired,
};
